import React, { useState } from "react";
import axios from "axios";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import {
  Tv,
  Phone,
  Clock,
  MapPin,
  Star,
  CheckCircle2,
  Wrench,
  Shield,
  Zap,
  BadgeCheck,
  Cpu,
  Plug,
  Monitor,
  Settings2,
  ArrowRight,
  MessageCircle,
  Users,
  Loader2,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import {
  NAV,
  HERO,
  SERVICES,
  WHY,
  BRANDS,
  AREAS,
  CTA,
  LEAD,
  FOOTER,
  MOBILE_BAR,
} from "@/constants/testIds";
import { trackPhoneClick, trackWhatsAppClick, trackLeadSubmit } from "@/lib/track";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHONE = "+91 70260 64001";
const PHONE_TEL = "+917026064001";
const WHATSAPP_LINK =
  "https://wa.me/917026064001?text=Hi%20TV%20Service%20Bangalore%2C%20I%20need%20a%20TV%20repair%20service.";
const MAP_LINK = "https://maps.google.com/?q=Bangalore";

/* ---------------- HEADER ---------------- */
const Header = () => {
  return (
    <>
      {/* Top thin bar */}
      <div className="hidden md:block bg-gradient-to-r from-slate-50 to-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between text-xs text-slate-600">
          <div className="flex items-center gap-5">
            <span className="inline-flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-blue-600" />
              Mon – Sat · 9:00 AM – 7:00 PM
            </span>
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-blue-600" />
              Serving All of Bangalore
            </span>
          </div>
          <a
            href={`tel:${PHONE_TEL}`}
            onClick={() => trackPhoneClick("header-top-bar")}
            className="inline-flex items-center gap-1.5 font-semibold text-slate-800 hover:text-blue-600"
          >
            <Phone className="w-3.5 h-3.5 text-blue-600" />
            {PHONE}
          </a>
        </div>
      </div>

      {/* Main header */}
      <header className="sticky top-0 z-50 header-glass">
        <div className="max-w-7xl mx-auto px-6 h-[72px] flex items-center justify-between">
          <a
            href="#top"
            data-testid={NAV.logo}
            className="flex items-center gap-3"
          >
            <div className="w-11 h-11 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-sm shadow-blue-500/30">
              <Tv className="w-6 h-6" strokeWidth={2.2} />
            </div>
            <div className="leading-tight">
              <div className="text-[17px] font-extrabold tracking-wide text-slate-900">
                TV SERVICE
              </div>
              <div className="text-[10px] font-semibold tracking-[0.22em] text-blue-600">
                BANGALORE
              </div>
            </div>
          </a>

          <nav className="hidden lg:flex items-center gap-9 text-[15px] font-medium text-slate-700">
            <a
              href="#services"
              data-testid={NAV.servicesLink}
              className="hover:text-blue-600 transition-colors"
            >
              Services
            </a>
            <a
              href="#why-us"
              data-testid={NAV.whyUsLink}
              className="hover:text-blue-600 transition-colors"
            >
              Why Us
            </a>
            <a
              href="#areas"
              data-testid={NAV.areasLink}
              className="hover:text-blue-600 transition-colors"
            >
              Areas
            </a>
            <a
              href="#brands"
              data-testid={NAV.brandsLink}
              className="hover:text-blue-600 transition-colors"
            >
              Brands
            </a>
            <a
              href="#contact"
              data-testid={NAV.contactLink}
              className="hover:text-blue-600 transition-colors"
            >
              Contact
            </a>
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={`tel:${PHONE_TEL}`}
              onClick={() => trackPhoneClick("header-cta")}
              data-testid={NAV.bookButton}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold shadow-md shadow-blue-500/25 transition-all hover:-translate-y-0.5"
            >
              <Phone className="w-4 h-4" /> {PHONE}
            </a>
          </div>
        </div>
      </header>
    </>
  );
};

/* ---------------- HERO ---------------- */
const Hero = () => {
  return (
    <section id="top" className="hero-grad text-white relative overflow-hidden">
      {/* faint TV image overlay */}
      <div
        className="absolute inset-0 opacity-25 bg-cover bg-center"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1593359677879-a4bb92f829d1?q=80&w=2000&auto=format&fit=crop)",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#050912]/95 via-[#050912]/80 to-[#050912]/40" />

      <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-24 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
        <div className="fade-up">
          <div
            data-testid={HERO.badge}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-amber-400/30 bg-amber-400/10 text-amber-300 text-xs font-semibold tracking-wide"
          >
            <Star className="w-3.5 h-3.5 fill-amber-300" />
            Bangalore's Most Trusted TV Repair · Since 2014
          </div>

          <h1
            data-testid={HERO.title}
            className="mt-6 text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] font-extrabold tracking-tight"
          >
            Expert TV Repair{" "}
            <span className="text-blue-400">at Your Doorstep</span>
          </h1>

          <p className="mt-6 text-slate-300 text-[17px] max-w-xl leading-relaxed">
            LED, LCD, OLED & QLED — motherboard, panel, power supply or
            software. Same-day diagnosis, transparent pricing and
            warranty-backed repair across Bangalore.
          </p>

          <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-8 text-[15px] text-slate-200">
            {[
              "LED · LCD · OLED · QLED",
              "Genuine OEM components",
              "Free doorstep diagnosis",
              "Up to 6 months warranty",
            ].map((f) => (
              <li key={f} className="inline-flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-blue-400 flex-shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <div className="mt-9 flex flex-wrap gap-3">
            <a
              href="#contact"
              data-testid={HERO.bookButton}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3.5 rounded-xl font-semibold shadow-lg shadow-blue-600/30 transition-all hover:-translate-y-0.5"
            >
              Book a Service <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* Hero image with floating card */}
        <div className="relative fade-up" style={{ animationDelay: "120ms" }}>
          <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-black/40 ring-1 ring-white/10">
            <img
              src="/technician-hero.jpg"
              alt="TV Service Bangalore technician greeting customer with folded hands"
              className="w-full h-[420px] object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          </div>

          <div className="absolute -bottom-6 left-4 right-4 sm:left-6 sm:right-auto sm:w-[360px] bg-slate-900/80 backdrop-blur-md border border-white/10 rounded-2xl px-5 py-4 shadow-xl flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-400 text-slate-900 flex items-center justify-center">
              <BadgeCheck className="w-7 h-7" strokeWidth={2.4} />
            </div>
            <div>
              <div className="text-white font-bold text-lg leading-tight">
                100% Satisfaction
              </div>
              <div className="text-slate-300 text-xs mt-0.5">
                4,800+ happy customers across Bangalore
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- FEATURES (4-card) ---------------- */
const features = [
  {
    icon: Wrench,
    title: "Expert Technicians",
    desc: "10+ years repairing flagship TV brands.",
  },
  {
    icon: Shield,
    title: "Genuine Parts",
    desc: "Only OEM-grade boards & panels.",
  },
  {
    icon: Zap,
    title: "Same-Day Service",
    desc: "Most repairs completed within 24 hours.",
  },
  {
    icon: BadgeCheck,
    title: "Warranty Backed",
    desc: "Up to 6 months service warranty.",
  },
];

const Features = () => (
  <section className="bg-white border-b border-slate-100">
    <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
      {features.map((f, i) => {
        const Icon = f.icon;
        return (
          <div
            key={f.title}
            data-testid={`feature-${i}`}
            className="rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_6px_20px_-12px_rgba(15,23,42,0.18)] hover:shadow-[0_18px_40px_-20px_rgba(37,99,235,0.45)] hover:-translate-y-1 transition-all duration-300"
          >
            <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-4">
              <Icon className="w-6 h-6" strokeWidth={2.1} />
            </div>
            <h3 className="text-slate-900 font-bold text-lg">{f.title}</h3>
            <p className="text-slate-500 text-sm mt-1.5 leading-relaxed">
              {f.desc}
            </p>
          </div>
        );
      })}
    </div>
  </section>
);

/* ---------------- SERVICES ---------------- */
const services = [
  {
    slug: "motherboard",
    icon: Cpu,
    title: "Motherboard Repair",
    desc: "Chip-level diagnostics & repair for all LED/LCD/OLED/QLED TV motherboards.",
    image:
      "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=1400&auto=format&fit=crop",
  },
  {
    slug: "power-supply",
    icon: Plug,
    title: "Power Supply Repair",
    desc: "Faulty SMPS, no-power, blinking standby — fixed with original components.",
    image:
      "https://images.unsplash.com/photo-1555617981-dac3880eac6e?q=80&w=1400&auto=format&fit=crop",
  },
  {
    slug: "display",
    icon: Monitor,
    title: "Display Replacement",
    desc: "Cracked / dead panels swapped with genuine displays. Warranty included.",
    image:
      "https://images.unsplash.com/photo-1593359677879-a4bb92f829d1?q=80&w=1400&auto=format&fit=crop",
  },
  {
    slug: "software",
    icon: Settings2,
    title: "Software Update",
    desc: "Smart TV firmware flashing, app issues, slowness & boot-loop resolved.",
    image:
      "https://images.unsplash.com/photo-1461151304267-38535e780c79?q=80&w=1400&auto=format&fit=crop",
  },
];

const Services = () => (
  <section
    id="services"
    data-testid={SERVICES.section}
    className="bg-white py-20"
  >
    <div className="max-w-7xl mx-auto px-6">
      <div className="max-w-2xl">
        <div className="text-xs font-bold tracking-[0.2em] text-blue-600">
          WHAT WE FIX
        </div>
        <h2 className="mt-3 text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">
          Our <span className="text-blue-600">Services</span>
        </h2>
        <p className="mt-4 text-slate-600 leading-relaxed">
          Every TV repair is handled by trained technicians using genuine parts
          — from chip-level fixes to full panel replacement.
        </p>
      </div>

      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {services.map((s) => {
          const Icon = s.icon;
          return (
            <div
              key={s.slug}
              data-testid={SERVICES.card(s.slug)}
              className="group rounded-2xl bg-white border border-slate-100 overflow-hidden shadow-[0_6px_24px_-14px_rgba(15,23,42,0.2)] hover:shadow-[0_24px_48px_-20px_rgba(37,99,235,0.45)] hover:-translate-y-1.5 transition-all duration-300"
            >
              <div className="relative h-44 overflow-hidden">
                <img
                  src={s.image}
                  alt={s.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                    <Icon className="w-5 h-5" strokeWidth={2.2} />
                  </div>
                  <h3 className="text-slate-900 font-bold text-lg">
                    {s.title}
                  </h3>
                </div>
                <p className="mt-3 text-slate-500 text-sm leading-relaxed">
                  {s.desc}
                </p>
                <a
                  href="#contact"
                  data-testid={SERVICES.bookLink(s.slug)}
                  className="mt-5 inline-flex items-center gap-1.5 text-blue-600 font-semibold text-sm group-hover:gap-2.5 transition-all"
                >
                  Book this service <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-12 flex justify-center">
        <a
          href="#contact"
          data-testid={SERVICES.freeDiagnosisButton}
          className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-7 py-3.5 rounded-xl font-semibold transition-all hover:-translate-y-0.5"
        >
          Get a Free Diagnosis <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  </section>
);

/* ---------------- WHY US ---------------- */
const WhyUs = () => {
  const points = [
    "10+ years repairing premium and budget TVs",
    "Transparent, upfront pricing — no hidden charges",
    "Free doorstep pickup across Bangalore",
    "Trained technicians, sanitised tools",
    "All major brands and panel sizes supported",
  ];
  return (
    <section
      id="why-us"
      data-testid={WHY.section}
      className="bg-slate-50 py-20"
    >
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
        <div className="relative">
          <div className="rounded-3xl overflow-hidden shadow-xl ring-1 ring-slate-200">
            <img
              src="https://images.unsplash.com/photo-1581092580497-e0d23cbdf1dc?q=80&w=1400&auto=format&fit=crop"
              alt="TV repair technician"
              className="w-full h-[460px] object-cover"
            />
          </div>
          <div className="absolute -bottom-5 -right-2 sm:right-6 bg-white rounded-2xl px-5 py-4 shadow-xl flex items-center gap-4 ring-1 ring-slate-100">
            <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center">
              <Users className="w-6 h-6" strokeWidth={2.2} />
            </div>
            <div>
              <div className="text-2xl font-extrabold text-slate-900 leading-none">
                4,800+
              </div>
              <div className="text-xs text-slate-500 mt-1">TVs repaired</div>
            </div>
          </div>
        </div>

        <div>
          <div className="text-xs font-bold tracking-[0.2em] text-blue-600">
            WHY CHOOSE US
          </div>
          <h2 className="mt-3 text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight">
            Repairs you can <span className="text-blue-600">actually trust</span>
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed">
            We've kept Bangalore homes entertained for over a decade — fixing
            everything from no-display issues to complex motherboard failures.
          </p>

          <ul className="mt-8 space-y-4">
            {points.map((p) => (
              <li key={p} className="flex items-start gap-3">
                <div className="mt-0.5 w-6 h-6 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-4 h-4" strokeWidth={2.5} />
                </div>
                <span className="text-slate-700">{p}</span>
              </li>
            ))}
          </ul>

          <div className="mt-9 flex flex-wrap gap-3">
            <a
              href="#contact"
              data-testid={WHY.bookButton}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold shadow-md shadow-blue-500/25 transition-all hover:-translate-y-0.5"
            >
              Book a Service
            </a>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackWhatsAppClick("why-us")}
              data-testid={WHY.whatsappButton}
              className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-800 px-6 py-3 rounded-xl font-semibold transition-all"
            >
              <MessageCircle className="w-4 h-4 text-emerald-600" />
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- SERVICE AREAS ---------------- */
const SERVICE_AREAS = [
  "Koramangala",
  "Indiranagar",
  "Whitefield",
  "HSR Layout",
  "BTM Layout",
  "Jayanagar",
  "JP Nagar",
  "Marathahalli",
  "Electronic City",
  "Bellandur",
  "Sarjapur Road",
  "Bannerghatta Road",
  "Hebbal",
  "Yelahanka",
  "Rajajinagar",
  "Malleshwaram",
  "Banashankari",
  "Basavanagudi",
  "MG Road",
  "Brookefield",
  "Kalyan Nagar",
  "Frazer Town",
  "RT Nagar",
  "Ulsoor",
];

const areaSlug = (name) =>
  name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

const ServiceAreas = () => (
  <section
    id="areas"
    data-testid={AREAS.section}
    className="bg-white py-20 border-t border-slate-100"
  >
    <div className="max-w-7xl mx-auto px-6">
      <div className="text-center max-w-3xl mx-auto">
        <div className="text-xs font-bold tracking-[0.22em] text-blue-600">
          SERVICE AREAS
        </div>
        <h2 className="mt-3 text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight">
          Doorstep TV Repair Across{" "}
          <span className="text-blue-600">All of Bangalore</span>
        </h2>
        <p className="mt-5 text-slate-600 leading-relaxed">
          Same-day service, ₹300 visit charge, genuine parts. Tap your area to
          call us instantly — technician arrives within a few hours.
        </p>
      </div>

      <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {SERVICE_AREAS.map((area) => {
          const slug = areaSlug(area);
          return (
            <a
              key={slug}
              href={`tel:${PHONE_TEL}`}
              onClick={() => trackPhoneClick(`area-${slug}`)}
              data-testid={AREAS.card(slug)}
              aria-label={`Call TV Service Bangalore for repair in ${area}`}
              className="group relative flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm hover:shadow-md hover:border-blue-300 hover:bg-blue-50 transition-all"
            >
              <MapPin
                className="w-4 h-4 text-blue-600 flex-shrink-0"
                strokeWidth={2.2}
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-900 truncate">
                  {area}
                </div>
                <div className="text-[11px] text-slate-500 group-hover:text-blue-700 truncate">
                  Book service
                </div>
              </div>
              <Phone
                className="w-4 h-4 text-slate-400 group-hover:text-blue-600 flex-shrink-0"
                strokeWidth={2.2}
              />
            </a>
          );
        })}
      </div>

      <p className="mt-10 text-center text-sm text-slate-500">
        Not seeing your area?{" "}
        <a
          href={`tel:${PHONE_TEL}`}
          onClick={() => trackPhoneClick("area-fallback")}
          className="text-blue-600 font-semibold hover:underline"
        >
          Call {PHONE}
        </a>{" "}
        — we cover all of Bangalore.
      </p>
    </div>
  </section>
);

/* ---------------- BRANDS MARQUEE ---------------- */
const BRAND_LIST = [
  "SAMSUNG",
  "LG",
  "SONY",
  "SONY BRAVIA",
  "PANASONIC",
  "TCL",
  "MI",
  "PHILIPS",
  "VU",
  "HAIER",
  "HISENSE",
  "ONEPLUS",
];

const Brands = () => (
  <section
    id="brands"
    data-testid={BRANDS.section}
    className="dark-section text-white py-20 overflow-hidden"
  >
    <div className="max-w-7xl mx-auto px-6 text-center">
      <div className="text-xs font-bold tracking-[0.22em] text-blue-400">
        WE SERVICE EVERY MAJOR BRAND
      </div>
      <h2 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight">
        Brands We <span className="text-blue-400">Repair</span>
      </h2>
    </div>

    <div className="mt-12 relative">
      <div className="marquee-track flex gap-16 px-6">
        {[...BRAND_LIST, ...BRAND_LIST].map((b, i) => (
          <span
            key={`${b}-${i}`}
            className="text-4xl sm:text-5xl font-extrabold tracking-wider text-white/15 hover:text-white/40 whitespace-nowrap transition-colors"
          >
            {b}
          </span>
        ))}
      </div>
      {/* fade edges */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#060a14] to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#060a14] to-transparent" />
    </div>
  </section>
);

/* ---------------- LEAD FORM + CTA ---------------- */
const TV_BRANDS = [
  "Samsung", "LG", "Sony", "Sony Bravia", "Panasonic", "TCL",
  "Mi", "Philips", "Vu", "Haier", "Hisense", "OnePlus", "Other",
];

const ISSUES = [
  { value: "motherboard", label: "Motherboard / no display" },
  { value: "power-supply", label: "Power supply / no power" },
  { value: "display", label: "Cracked / dead panel" },
  { value: "software", label: "Software / smart TV issue" },
  { value: "installation", label: "Installation / mounting" },
  { value: "other", label: "Other / not sure" },
];

const LeadForm = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    tv_brand: "",
    issue_type: "other",
    location: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (form.name.trim().length < 2) {
      toast.error("Please enter your full name.");
      return;
    }
    const cleanedPhone = form.phone.replace(/[\s\-()]+/g, "");
    if (!/^\+?\d{7,15}$/.test(cleanedPhone)) {
      toast.error("Please enter a valid phone number.");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/leads`, {
        name: form.name.trim(),
        phone: cleanedPhone,
        tv_brand: form.tv_brand || null,
        issue_type: form.issue_type,
        location: form.location || null,
        message: form.message || null,
      });
      setSubmitted(true);
      toast.success("Thanks! Our team will call you within 15 minutes.");
      trackLeadSubmit(form.issue_type);
      setForm({
        name: "",
        phone: "",
        tv_brand: "",
        issue_type: "other",
        location: "",
        message: "",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Could not submit. Please call us directly."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      data-testid={LEAD.form}
      className="bg-white rounded-2xl p-6 sm:p-8 shadow-2xl ring-1 ring-slate-100 text-left"
    >
      <h3 className="text-slate-900 text-2xl font-extrabold tracking-tight">
        Book a Free Diagnosis
      </h3>
      <p className="mt-1.5 text-sm text-slate-500">
        Fill the form — we'll call you back within 15 minutes.
      </p>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-1">
          <label className="block text-xs font-semibold text-slate-700 mb-1.5">
            Your Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            required
            value={form.name}
            onChange={update("name")}
            placeholder="Ramesh Kumar"
            data-testid={LEAD.name}
            className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
          />
        </div>

        <div className="sm:col-span-1">
          <label className="block text-xs font-semibold text-slate-700 mb-1.5">
            Phone Number <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            required
            value={form.phone}
            onChange={update("phone")}
            placeholder="+91 98XXXXXXXX"
            data-testid={LEAD.phone}
            className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1.5">
            TV Brand
          </label>
          <select
            value={form.tv_brand}
            onChange={update("tv_brand")}
            data-testid={LEAD.brand}
            className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-900 bg-white focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
          >
            <option value="">Select brand</option>
            {TV_BRANDS.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1.5">
            Issue Type
          </label>
          <select
            value={form.issue_type}
            onChange={update("issue_type")}
            data-testid={LEAD.issue}
            className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-900 bg-white focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
          >
            {ISSUES.map((i) => (
              <option key={i.value} value={i.value}>{i.label}</option>
            ))}
          </select>
        </div>

        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold text-slate-700 mb-1.5">
            Area in Bangalore
          </label>
          <input
            type="text"
            value={form.location}
            onChange={update("location")}
            placeholder="e.g. Indiranagar, Whitefield, HSR Layout"
            data-testid={LEAD.location}
            className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
          />
        </div>

        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold text-slate-700 mb-1.5">
            Describe the issue
          </label>
          <textarea
            rows={3}
            value={form.message}
            onChange={update("message")}
            placeholder="No display, sound works fine..."
            data-testid={LEAD.message}
            className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all resize-none"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid={LEAD.submit}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-70 text-white px-6 py-3.5 rounded-xl font-semibold shadow-lg shadow-blue-600/25 transition-all hover:-translate-y-0.5"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Sending...
          </>
        ) : (
          <>
            Request Free Callback <ArrowRight className="w-4 h-4" />
          </>
        )}
      </button>

      {submitted && (
        <div
          data-testid={LEAD.success}
          className="mt-4 flex items-start gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-emerald-800 text-sm"
        >
          <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
          <span>
            Got it! A technician will call you shortly to confirm the visit.
          </span>
        </div>
      )}

      <p className="mt-4 text-[11px] text-slate-400 text-center">
        Free doorstep visit · No charges for diagnosis
      </p>
    </form>
  );
};

/* ---------------- CTA ---------------- */
const CTASection = () => (
  <section
    id="contact"
    data-testid={CTA.section}
    className="cta-grad text-white py-24 relative overflow-hidden"
  >
    <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.4),transparent_60%)]" />
    <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-xs font-semibold tracking-wide">
          <Zap className="w-3.5 h-3.5 text-amber-300" />
          Same-day service available
        </div>
        <h2 className="mt-5 text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">
          Need TV Repair <span className="text-amber-300">Today?</span>
        </h2>
        <p className="mt-5 text-blue-100 text-lg leading-relaxed max-w-md">
          Call us now for a free diagnosis or book online — our technician will
          be at your doorstep within 90 minutes.
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href={`tel:${PHONE_TEL}`}
            onClick={() => trackPhoneClick("cta-section")}
            data-testid={CTA.callButton}
            className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-slate-900 px-6 py-3.5 rounded-xl font-bold shadow-xl transition-all hover:-translate-y-0.5"
          >
            <Phone className="w-5 h-5 text-blue-600" /> Call {PHONE}
          </a>
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackWhatsAppClick("cta-section")}
            data-testid={CTA.bookButton}
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-6 py-3.5 rounded-xl font-bold transition-all hover:-translate-y-0.5"
          >
            <MessageCircle className="w-4 h-4 text-emerald-400" />
            WhatsApp Us
          </a>
        </div>

        <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
          {[
            ["4,800+", "TVs repaired"],
            ["90 min", "Avg. arrival"],
            ["6 mo", "Warranty"],
          ].map(([n, l]) => (
            <div key={l}>
              <div className="text-2xl sm:text-3xl font-extrabold text-amber-300">{n}</div>
              <div className="text-xs text-blue-100 mt-1">{l}</div>
            </div>
          ))}
        </div>
      </div>

      <div data-testid={LEAD.section}>
        <LeadForm />
      </div>
    </div>
  </section>
);

/* ---------------- FOOTER ---------------- */
const Footer = () => (
  <footer
    data-testid={FOOTER.section}
    className="bg-[#050912] text-slate-300 pt-16 pb-10"
  >
    <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
      <div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-blue-600 text-white flex items-center justify-center">
            <Tv className="w-6 h-6" strokeWidth={2.2} />
          </div>
          <div className="leading-tight">
            <div className="text-white text-[17px] font-extrabold tracking-wide">
              TV SERVICE
            </div>
            <div className="text-[10px] font-semibold tracking-[0.22em] text-blue-400">
              BANGALORE
            </div>
          </div>
        </div>
        <p className="mt-5 text-sm text-slate-400 leading-relaxed">
          Bangalore's trusted TV repair experts since 2014. Genuine parts,
          transparent pricing, doorstep service.
        </p>
      </div>

      <div>
        <h4 className="text-white font-bold">Quick Links</h4>
        <ul className="mt-5 space-y-3 text-sm">
          <li><a href="#services" className="hover:text-white">Services</a></li>
          <li><a href="#why-us" className="hover:text-white">Why Us</a></li>
          <li><a href="#areas" className="hover:text-white">Service Areas</a></li>
          <li><a href="#brands" className="hover:text-white">Brands</a></li>
          <li><a href="#contact" className="hover:text-white">Book a Service</a></li>
        </ul>
      </div>

      <div>
        <h4 className="text-white font-bold">Our Services</h4>
        <ul className="mt-5 space-y-3 text-sm">
          <li>Motherboard Repair</li>
          <li>Power Supply Repair</li>
          <li>Display Replacement</li>
          <li>Software Update</li>
          <li>TV Installation</li>
        </ul>
      </div>

      <div>
        <h4 className="text-white font-bold">Contact</h4>
        <ul className="mt-5 space-y-3 text-sm">
          <li className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-blue-400 mt-0.5" />
            Doorstep service across Bangalore
          </li>
          <li>
            <a
              href={`tel:${PHONE_TEL}`}
              onClick={() => trackPhoneClick("footer")}
              className="flex items-center gap-2 hover:text-white"
            >
              <Phone className="w-4 h-4 text-blue-400" />
              {PHONE}
            </a>
          </li>
          <li>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackWhatsAppClick("footer")}
              className="flex items-center gap-2 hover:text-white"
            >
              <MessageCircle className="w-4 h-4 text-emerald-400" />
              WhatsApp Chat
            </a>
          </li>
          <li className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-blue-400" />
            Mon – Sat · 9 AM – 7 PM
          </li>
        </ul>
      </div>
    </div>

    <div className="max-w-7xl mx-auto px-6 mt-12 pt-6 border-t border-white/10">
      <div
        data-testid="footer-disclaimer"
        className="rounded-xl bg-white/[0.03] border border-white/10 px-5 py-4 text-[12px] leading-relaxed text-slate-400"
      >
        <span className="block text-amber-300 font-bold tracking-wide text-[11px] uppercase mb-1.5">
          Disclaimer
        </span>
        TV Service Bangalore is an independent, private, out-of-warranty TV
        repair provider. We are not authorized or associated with any
        manufacturer. Brand names are used only for identification. We do not
        handle warranty products, do not claim to be an authorized service
        center, and do not issue manufacturer bills. We provide transparent,
        professional, and affordable private repair services with a ₹300
        inspection fee and doorstep support by our own trained technicians.
      </div>
      <div className="mt-5 text-xs text-slate-500 text-center">
        © {new Date().getFullYear()} TV Service Bangalore. All Rights Reserved.
      </div>
    </div>
  </footer>
);

/* ---------------- MOBILE BOTTOM BAR ---------------- */
const MobileBar = () => (
  <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 shadow-[0_-6px_16px_-10px_rgba(15,23,42,0.15)]">
    <div className="grid grid-cols-3 text-[11px] font-semibold text-slate-700">
      <a
        href={`tel:${PHONE_TEL}`}
        onClick={() => trackPhoneClick("mobile-bottom-bar")}
        data-testid={MOBILE_BAR.call}
        className="flex flex-col items-center justify-center py-2.5 gap-1"
      >
        <Phone className="w-4 h-4 text-blue-600" /> Call
      </a>
      <a
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noreferrer"
        onClick={() => trackWhatsAppClick("mobile-bottom-bar")}
        data-testid={MOBILE_BAR.whatsapp}
        className="flex flex-col items-center justify-center py-2.5 gap-1"
      >
        <MessageCircle className="w-4 h-4 text-emerald-600" /> WhatsApp
      </a>
      <a
        href={MAP_LINK}
        target="_blank"
        rel="noreferrer"
        data-testid={MOBILE_BAR.map}
        className="flex flex-col items-center justify-center py-2.5 gap-1"
      >
        <MapPin className="w-4 h-4 text-blue-600" /> Location
      </a>
    </div>
  </div>
);

/* ---------------- PAGE ---------------- */
const Home = () => (
  <div className="bg-white">
    <Header />
    <Hero />
    <Features />
    <Services />
    <WhyUs />
    <ServiceAreas />
    <Brands />
    <CTASection />
    <Footer />
    <MobileBar />
    {/* Spacer so content isn't hidden behind mobile bar */}
    <div className="h-14 lg:hidden" />
  </div>
);

function App() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
